.panel {
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

/* `panelTitle` is DELETED (#2283 §3/§4, applied by #2349): this panel is a
   `PageSection` now, so its title is a real heading with the shared spacing
   contract rather than a private title system. `sectionHierarchy.test.ts`
   asserts the class can never be declared here again. */

.summary,
.empty,
.note {
  margin: 0;
  font-size: var(--fs-sm);
}

.summary { color: var(--ink); }
.empty,
.note { color: var(--muted); }

.list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
}

  /* MEASURED white-on-white (#2156): this sits inside a --surface Surface, which paints the
     same --surface it does, so its ground separates it from nothing. F-43's
     exception is a declared line, and this is one. */
.item { border: 1px solid var(--hair);
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
  padding: var(--sp-2);
  border-radius: var(--r-sm);
  background: var(--surface);
}

.itemHead {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--sp-2);
}

.key {
  font-family: var(--font-mono);
  font-size: var(--fs-sm);
}

.kind {
  color: var(--muted);
  font-size: var(--fs-sm);
}

/* Tone is DATA on the element, not three class names: the server's vocabulary
   can grow, and an unknown verdict must still render — greyed, labelled with
   its own code, never invisible. */
.state {
  padding: 0 var(--sp-2);
  border-radius: var(--r-pill);
  border: 1px solid var(--hair);
  font-size: var(--fs-sm);
  font-weight: 600;
}
.state[data-tone='good'] { color: var(--good); border-color: var(--good); }
.state[data-tone='wait'] { color: var(--info); border-color: var(--info); }
.state[data-tone='bad']  { color: var(--crit); border-color: var(--crit); }

.says,
.alt,
.altMissing {
  margin: 0;
  font-size: var(--fs-sm);
}
.says { color: var(--ink); }
.alt { color: var(--muted); }
.altMissing { color: var(--crit); }

.altLabel {
  font-weight: 600;
  color: var(--ink);
}

/* Declared beside found. A definition list because that is what it is: two
   labelled facts about one thing, and a screen reader should read the label. */
.diff {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-3);
  font-size: var(--fs-sm);
}
.diff > div {
  display: flex;
  gap: var(--sp-1);
}
.diff dt {
  margin: 0;
  color: var(--muted);
}
.diff dd {
  margin: 0;
  font-variant-numeric: tabular-nums;
}

/* ── the attach control ──────────────────────────────────────────────────────
   The `<input type="file">` is the real control — it holds the accessible name,
   takes focus and takes the activation. It is hidden by CLIPPING rather than by
   `display: none`, because a display-none input is not focusable and the whole
   zone would become mouse-only. The visible ring therefore has to be drawn by
   the label on `:focus-within`. */
.attach {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

.drop {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Point 10's floor. A drop target smaller than a fingertip is a drop target
     for a mouse only. */
  min-height: 44px;
  padding: var(--sp-3) var(--sp-2);
  border: 1px dashed var(--border);
  border-radius: var(--r-sm);
  background: var(--surface);
  color: var(--muted);
  font-size: var(--fs-sm);
  text-align: center;
  cursor: pointer;
}

/* `var(--focus-ring)`, never `var(--brand)` — the brand measures 1.71:1 against
   the Coccolare dark surface and would fail WCAG 1.4.11 as an indicator. */
.drop:focus-within {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.drop[data-over] {
  border-style: solid;
  border-color: var(--focus-ring);
  color: var(--ink);
}

.drop[data-busy] {
  cursor: progress;
}

.file {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.dropText {
  pointer-events: none;
}

/* The live region is ALWAYS in the tree, empty when there is nothing to say —
   a region inserted at the moment it gains text is frequently not announced. */
.said {
  margin: 0;
  min-height: 1lh;
  font-size: var(--fs-sm);
  color: var(--muted);
}
.said[data-tone='good'] { color: var(--good); }
.said[data-tone='wait'] { color: var(--info); }
.said[data-tone='bad']  { color: var(--crit); }
